<template>
  <div class="rc-login">
    <RcContentHeader
      :title="$t(`auth.log.in`)"
      :large="true"
      :breadcrumbs="false"
    />

    <div class="rc-login--body">
      <div class="rc-intro">
        <div v-if="domains.length">
          <p v-html="$t('auth.subscriber.access.connect_to_usign')"></p>
          <ul class="rc-list-arrow">
            <li v-for="(domain, index) in domains" :key="index">
              @{{ domain }}
            </li>
          </ul>
        </div>
        <p v-else v-html="$t('auth.email.work')"></p>
      </div>

      <form class="rc-form-compact" name="UserLoginForm" @submit.stop.prevent>
        <div class="row g-0 mb-2">
          <label for="email" class="col-form-label col-12 text-align-left">{{
            $t("auth.email.address")
          }}</label>
          <div class="col">
            <input
              id="email"
              ref="email"
              name="email"
              type="email"
              class="form-control form-control-lg"
              :placeholder="placeholderDomain"
              :value="v$.form.email.$model"
              :class="validateState('email') != false ? '' : 'is-invalid'"
              :aria-invalid="validateState('email') != false ? null : true"
              :aria-describedby="
                validateState('email') != false
                  ? null
                  : 'feedback_invalid__email'
              "
              @change="setValue('email', $event)"
            />

            <div
              v-if="validateState('email') == false"
              id="feedback_invalid__email"
              aria-live="assertive"
              aria-atomic="true"
              class="invalid-feedback d-block"
            >
              <div v-if="!v$.form.email.email.$response">
                {{ $t("auth.email.invalid") }}
              </div>
              <div v-if="!v$.form.email.required.$response">
                {{ $t("auth.email.required") }}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="rc-cta-wrapper mb-3">
            <RcDebounced
              style="transform: translate3d(0px, 0px, 0px)"
              :disabled="disabled"
              variant="primary"
              class="btn-lg"
              block
              :promise="getPin.bind()"
              ><span>{{ $t("common.continue") }}</span></RcDebounced
            >
          </div>
        </div>

        <slot name="link"></slot>

        <div v-if="$slots.providers">
          <div class="rc-divider-or">
            <div class="rc-divider-or--line"></div>
            <div class="rc-divider-or--text">{{ $t("common.or") }}</div>
            <div class="rc-divider-or--line"></div>
          </div>

          <fieldset>
            <div class="row">
              <div class="col rc-providers">
                <slot name="providers"> </slot>
              </div>
            </div>
          </fieldset>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { AUTH_REQUEST, AUTH_REQUEST_PIN } from "@/libvue/apps/auth/actions";
import RcContentHeader from "#/layout/components/ContentHeader.vue";
import RcDebounced from "@/libvue/components/button/DebouncedButton.vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";

import moment from "moment";

export default {
  name: "RcLogin",

  components: {
    RcContentHeader,
    RcDebounced
  },
  setup() {
    return { v$: useVuelidate() };
  },

  /*
    Provider object must be structured as follow
    { text: "Some display text", url: "allauth url", returnUrl: "some url to return from" }
  */

  props: {
    domains: {
      type: Array,
      default: () => []
    }
  },
  emits: ["loginError"],

  data() {
    return {
      error: "",
      disabled: false,
      form: { email: "" }
    };
  },

  validations: {
    form: {
      email: { email, required }
    }
  },
  computed: {
    placeholderDomain() {
      if (this.domains.length) {
        return `${this.$t("common.name")}@${this.domains[0]}`;
      } else {
        return "";
      }
    }
  },

  methods: {
    validateState(name) {
      const { $dirty, $error } = this.v$.form[name];
      return $dirty ? !$error : null;
    },

    setValue(key, event) {
      let value = event.target.value;
      this.form[key] = value.toLowerCase();
      this.v$.form[key].$touch();
    },

    getPin() {
      this.v$.form.$touch();

      if (this.v$.form.$error) {
        this.$refs.email.focus();
        return;
      }

      this.disabled = true;
      const me = this;

      // make sure to clean tokens before login
      localStorage.removeItem("refresh-token");

      const authPinObj = JSON.parse(localStorage.getItem("auth-pin"));

      if (authPinObj && moment().isBefore(authPinObj.exp)) {
        return this.$router.push({
          name: "auth.login.pin",
          query: {
            redirect: this.$route.query.redirect
          }
        });
      } else if (authPinObj && moment().isAfter(authPinObj.exp)) {
        localStorage.removeItem("auth-pin");
      }

      return this.getPinKey({ email: this.form.email })
        .then(() => {
          me.$router.push({
            name: "auth.login.pin",
            query: { redirect: me.$route.query.redirect }
            // params: { email: me.form.email }
          });
        })
        .catch((error) => {
          me.error =
            error.response.data.detail ||
            me.$t("statics.loginError.authentication-error");
          me.$emit("loginError", {
            ...error.response.data,
            email: me.form.email
          });
          me.disabled = false;
        });
    },
    ...mapActions({
      local: AUTH_REQUEST,
      getPinKey: AUTH_REQUEST_PIN
    })
  }
};
</script>

<style lang="scss">
input[type="email"] {
  text-transform: lowercase;
  &::placeholder {
    text-transform: lowercase;
  }
}
</style>
